import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useFirstName, useStaffProfile } from '../hooks/useStaffProfile';
import { listIncidents, updateIncidentStatus, updateIncident, UpdateIncidentDTO } from '../api/incidents';
import { Incident, SEVERITY_OPTIONS, INCIDENT_TYPE_OPTIONS } from '../types/incident';
import { listMyAssignments, TrainingAssignment } from '../api/training';
import { getRoster } from '../api/roster';
import { CimtOnCallCard } from '../components/CimtOnCall';


interface DashboardProps {
  onReport:   () => void;
  onOpenChat: (incident: Incident) => void;
  onAdvisor?: () => void;
  /** Optional — when provided, the left-pane quick actions include a
   *  "My Training" button that jumps into the TrainingHub. */
  onTraining?: () => void;
  /** Optional — quick-link to Settings (admins only). */
  onSettings?: (tab?: 'staff' | 'invitations' | 'roster' | 'appearance') => void;
}

const SEVERITY_COLOURS: Record<string, string> = {
  CRITICAL: 'var(--accent-red)',
  MODERATE: 'var(--warning)',
  MINOR:    'var(--success)',
};

const STATUS_COLOURS: Record<string, string> = {
  OPEN:        'rgba(220,38,38,0.15)',
  IN_PROGRESS: 'rgba(234,88,12,0.15)',
  RESOLVED:    'rgba(22,163,74,0.15)',
  CLOSED:      'rgba(107,114,128,0.15)',
  REOPENED:    'rgba(245,158,11,0.15)',
};
const STATUS_TEXT: Record<string, string> = {
  OPEN:        'var(--accent-red)',
  IN_PROGRESS: 'var(--warning)',
  RESOLVED:    'var(--success)',
  CLOSED:      'var(--subtle)',
  REOPENED:    'var(--warning)',
};

function typeLabel(type: string): string {
  if (type === 'UNCLASSIFIED' || !type) return 'Awaiting classification…';
  return INCIDENT_TYPE_OPTIONS.find((o) => o.value === type)?.label
    ?? type.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Edit Incident Modal ────────────────────────────────────────────────────
interface EditModalProps {
  incident: Incident;
  onSave:   (updated: Incident) => void;
  onClose:  () => void;
}

function EditIncidentModal({ incident, onSave, onClose }: EditModalProps) {
  const [form, setForm]     = useState<UpdateIncidentDTO>({
    title:            incident.title,
    type:             incident.type,
    description:      incident.description,
    location:         incident.location,
    occurredAt:       incident.occurredAt?.slice(0, 16) ?? '',
    severity:         incident.severity,
    peopleInvolved:   incident.peopleInvolved   ?? '',
    immediateActions: incident.immediateActions ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState<string | null>(null);

  const field = (key: keyof UpdateIncidentDTO) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  async function handleSave() {
    if (!form.title?.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError(null);
    try {
      // Convert datetime-local (no TZ) to ISO with browser's TZ
      // Otherwise Lambda (UTC) misinterprets the time and shifts it
      const payload = {
        ...form,
        occurredAt: form.occurredAt ? new Date(form.occurredAt).toISOString() : form.occurredAt,
      };
      const updated = await updateIncident(incident.id, payload);
      onSave(updated);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', background: 'var(--bg)',
    border: '1px solid var(--border-md)', borderRadius: 8,
    color: 'var(--fg)', fontSize: 14, padding: '8px 12px',
    outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 700,
    color: 'var(--muted)', textTransform: 'uppercase',
    letterSpacing: 0.5, marginBottom: 5,
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '90vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'var(--card)', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Edit Incident</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{incident.id}</div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: '1px solid var(--border)', borderRadius: 8,
            color: 'var(--muted)', cursor: 'pointer', padding: '4px 10px', fontSize: 13,
          }}>✕</button>
        </div>

        {/* Form */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && (
            <div style={{
              background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)',
              borderRadius: 8, padding: '10px 14px', color: '#fca5a5', fontSize: 13,
            }}>{error}</div>
          )}

          <div>
            <label style={labelStyle}>Title *</label>
            <input value={form.title ?? ''} onChange={field('title')} style={inputStyle} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Type</label>
              <select value={form.type ?? ''} onChange={field('type')} style={inputStyle}>
                {INCIDENT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Severity</label>
              <select value={form.severity ?? ''} onChange={field('severity')} style={inputStyle}>
                {SEVERITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Location</label>
              <input value={form.location ?? ''} onChange={field('location')} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Date &amp; Time</label>
              <input type="datetime-local" value={form.occurredAt ?? ''} onChange={field('occurredAt')} style={inputStyle} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description ?? ''} onChange={field('description')} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>

          <div>
            <label style={labelStyle}>People Involved</label>
            <textarea value={form.peopleInvolved ?? ''} onChange={field('peopleInvolved')} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Names, roles, contact details…" />
          </div>

          <div>
            <label style={labelStyle}>Immediate Actions Taken</label>
            <textarea value={form.immediateActions ?? ''} onChange={field('immediateActions')} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="First-aid, notifications, evacuations…" />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 20px', borderTop: '1px solid var(--border)',
          display: 'flex', gap: 10, justifyContent: 'flex-end',
          background: 'var(--card)', flexShrink: 0,
        }}>
          <button onClick={onClose} style={{
            background: 'none', border: '1px solid var(--border)', borderRadius: 8,
            color: 'var(--muted)', cursor: 'pointer', padding: '7px 18px',
            fontSize: 13, fontFamily: 'inherit',
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            background: saving ? 'var(--faint)' : 'var(--primary)', border: 'none', borderRadius: 8,
            color: saving ? 'var(--muted)' : '#0a1a18',
            cursor: saving ? 'not-allowed' : 'pointer',
            padding: '7px 20px', fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
          }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────
export function Dashboard({ onOpenChat, onTraining, onSettings }: DashboardProps) {
  const { user }  = useAuth0();
  const me = useStaffProfile();
  const isAdmin = me?.cimaRole === 'ADMIN' || me?.cimaRole === 'MANAGER';
  const [incidents,  setIncidents]  = useState<Incident[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<Incident | null>(null);
  const [activeTab,  setActiveTab]  = useState<'recent' | 'resolved'>('recent');
  const [myTraining, setMyTraining] = useState<TrainingAssignment[]>([]);
  const [showAllIncidents, setShowAllIncidents] = useState(false);

  useEffect(() => {
    listIncidents()
      .then(setIncidents)
      .catch(() => setIncidents([]))
      .finally(() => setLoading(false));
    // Training assignments — failures are non-fatal; the tile just hides
    listMyAssignments().then(setMyTraining).catch(() => setMyTraining([]));
  }, []);

  // Live auto-refresh — silently re-pull incidents so the posture band, triage
  // queue and metrics stay current without a manual reload. Backs the
  // "Live · auto-refresh" tag in the header (no spinner; failures are ignored).
  useEffect(() => {
    const id = setInterval(() => {
      listIncidents().then(setIncidents).catch(() => { /* keep last-known */ });
    }, 45000);
    return () => clearInterval(id);
  }, []);

  // Sidebar "All Incidents" opens the breakdown modal from anywhere.
  useEffect(() => {
    const open = () => setShowAllIncidents(true);
    window.addEventListener('cima:open-all-incidents', open);
    return () => window.removeEventListener('cima:open-all-incidents', open);
  }, []);

  async function handleClose(inc: Incident) {
    setUpdatingId(inc.id);
    try {
      const updated = await updateIncidentStatus(inc.id, 'CLOSED');
      setIncidents((prev) => prev.map((i) => i.id === inc.id ? updated : i));
    } catch (err: any) {
      console.error('Status update failed:', err.message);
    } finally {
      setUpdatingId(null);
    }
  }

  async function handleReopen(inc: Incident) {
    setUpdatingId(inc.id);
    try {
      const updated = await updateIncidentStatus(inc.id, 'REOPENED');
      setIncidents((prev) => prev.map((i) => i.id === inc.id ? updated : i));
      setActiveTab('recent');
    } catch (err: any) {
      console.error('Status update failed:', err.message);
    } finally {
      setUpdatingId(null);
    }
  }

  function handleEditSaved(updated: Incident) {
    setIncidents((prev) => prev.map((i) => i.id === updated.id ? updated : i));
    setEditTarget(null);
  }

  const RECENT_STATUSES  = new Set(['OPEN', 'IN_PROGRESS', 'REOPENED']);
  const RESOLVED_STATUSES = new Set(['RESOLVED', 'CLOSED']);

  const recentIncidents   = incidents.filter((i) => RECENT_STATUSES.has(i.status));
  const resolvedIncidents = incidents.filter((i) => RESOLVED_STATUSES.has(i.status));
  const displayedIncidents = activeTab === 'recent' ? recentIncidents : resolvedIncidents;

  const counts = {
    total:    incidents.length,
    critical: incidents.filter((i) => i.severity === 'CRITICAL').length,
    open:     recentIncidents.length,
    resolved: resolvedIncidents.length,
  };

  // Use the resolved staff first name from cima_staff (matched by email at login).
  // Falls back to "there" — never shows the email address as a name.
  const firstName = useFirstName();
  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const nowLabel = now.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
    + ', ' + now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  // ── Derived panels (right side) ──
  const critical = incidents.filter(i => i.severity === 'CRITICAL' && RECENT_STATUSES.has(i.status));
  const activeForHero = critical[0];          // most critical active incident
  // Triage order — newest incident first. Top 4 show in the tile;
  // "View all" opens the full breakdown modal (unchanged).
  const activeList = [...recentIncidents]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 4);
  const recentResolved = resolvedIncidents.slice(0, 3);

  // ── Response posture — drives the hero band's reactive beacon ──
  // Any live critical incident → CRITICAL (red, pulsing). Other active
  // incidents → ACTIVE (amber). Nothing active → STEADY (calm green).
  const posture = critical.length >= 1
    ? { key: 'critical', label: 'Critical', tone: '#ED1C24' }
    : recentIncidents.length > 0
      ? { key: 'active', label: 'Active', tone: '#f59e0b' }
      : { key: 'steady', label: 'Steady', tone: '#22c55e' };
  const isCrit = posture.key === 'critical';
  const bandBorder = posture.key === 'steady'
    ? '1px solid rgba(59,130,246,0.35)'
    : `1px solid color-mix(in srgb, ${posture.tone} 55%, rgba(59,130,246,0.4))`;
  const bandShadow = posture.key === 'steady'
    ? '0 20px 44px -20px rgba(1,39,112,0.6)'
    : `0 20px 44px -20px rgba(1,39,112,0.55), 0 0 34px -6px color-mix(in srgb, ${posture.tone} 60%, transparent)`;

  // Stat-card sublabels.
  const inProgress = incidents.filter((i) => i.status === 'IN_PROGRESS').length;
  const resolutionRate = counts.total ? Math.round((counts.resolved / counts.total) * 100) : 0;
  const newThisWeek = incidents.filter((i) => Date.now() - new Date(i.createdAt).getTime() < 7 * 86400000).length;

  return (
    <div className="dash2 page-enter">

      {editTarget && (
        <EditIncidentModal
          incident={editTarget}
          onSave={handleEditSaved}
          onClose={() => setEditTarget(null)}
        />
      )}

      {showAllIncidents && (
        <AllIncidentsModal
          incidents={incidents}
          onClose={() => setShowAllIncidents(false)}
          onOpen={(inc) => { setShowAllIncidents(false); onOpenChat(inc); }}
        />
      )}

      {/* Header — branded "command console" hero band, reactive to posture */}
      <div data-tour="dash.console" style={{
        position: 'relative', overflow: 'hidden',
        borderRadius: 20, padding: '24px 28px',
        background: '#0153D4',
        border: bandBorder,
        boxShadow: bandShadow,
        animation: isCrit ? 'cimaBeaconPulse 2.1s ease-in-out infinite' : 'none',
        transition: 'border-color .3s, box-shadow .3s',
      }}>
        {/* ambient glow — the top-right bloom picks up the posture tone */}
        <div aria-hidden style={{ position: 'absolute', top: -70, right: -20, width: 240, height: 240, borderRadius: '50%', background: 'transparent', pointerEvents: 'none', transition: 'background .3s' }} />
        <div aria-hidden style={{ position: 'absolute', bottom: -90, left: '38%', width: 220, height: 220, borderRadius: '50%', background: 'transparent', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', rowGap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
            {/* Brand shield badge — halo tinted to posture; pulses when critical */}
            <span style={{
              width: 54, height: 54, borderRadius: 16, flexShrink: 0, display: 'grid', placeItems: 'center',
              background: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.22)',
              boxShadow: `0 0 0 4px color-mix(in srgb, ${posture.key === 'steady' ? '#0067FE' : posture.tone} 22%, transparent)`,
              animation: isCrit ? 'cimaCriticalDotPulse 1.6s infinite' : 'none',
            }}>
              <svg width="27" height="27" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" />
              </svg>
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: '0.02em', color: 'rgba(255,255,255,0.62)' }}>
                {greeting}, {firstName} · {nowLabel}
              </div>
              <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em', color: '#fff', lineHeight: 1.08, margin: '3px 0 0', textShadow: '0 2px 18px rgba(0,0,0,0.28)' }}>
                CIMT Console
              </h1>
            </div>
          </div>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600,
            color: '#fff', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)',
            padding: '8px 15px', borderRadius: 999, whiteSpace: 'nowrap', backdropFilter: 'blur(4px)',
          }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 8px #4ade80', animation: 'cimaCriticalDotPulse 1.6s infinite' }} />
            Live · auto-refresh
          </span>
        </div>
      </div>

        {/* Stat cards */}
        <div className="dash2-statgrid" data-tour="dash.glance">
          <MetricTile label="Total Incidents" value={counts.total}    sub={`+${newThisWeek} this week`}           tone="brand"   />
          <MetricTile label="Critical"        value={counts.critical} sub="Requires attention"                   tone="danger"  />
          <MetricTile label="Open"            value={counts.open}     sub={`${inProgress} in progress`}          tone="warning" />
          <MetricTile label="Resolved"        value={counts.resolved} sub={`${resolutionRate}% resolution rate`} tone="success" />
        </div>

        {/* Live incident command — active critical incident in detail */}
        <div data-tour="dash.livecommand">
          <LiveCommandPanel incident={activeForHero} onOpenChat={onOpenChat} />
        </div>

      {/* ───────── LOWER SPLIT: main (incidents) | side (CIMT, training, resolved) ───────── */}
      <div className="dash2-cols">
        <div className="dash2-main">

        {/* Triage Queue — active incidents, most severe first */}
        <section data-tour="dash.activelist">
          <SectionHeader
            title="Triage Queue"
            count={recentIncidents.length}
            action={recentIncidents.length > activeList.length ? (
              <button onClick={() => setShowAllIncidents(true)} style={{
                background: 'transparent', border: 'none', padding: 0,
                color: 'var(--primary)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
              }}>View all {recentIncidents.length} →</button>
            ) : undefined}
          />
          {loading ? (
            <EmptyTile text="Loading…" />
          ) : activeList.length === 0 ? (
            <EmptyTile text="No active incidents. Every report resolves eventually 🎉" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {activeList.map(inc => (
                <IncidentRow
                  key={inc.id}
                  inc={inc}
                  onOpen={() => onOpenChat(inc)}
                  onEdit={isAdmin ? () => setEditTarget(inc) : undefined}
                  onClose={isAdmin ? () => handleClose(inc) : undefined}
                  updating={updatingId === inc.id}
                />
              ))}
            </div>
          )}
        </section>

        {/* Incident volume trend */}
        <IncidentVolumeCard incidents={incidents} />
      </div>

      {/* ───────── SIDE COLUMN ───────── */}
      <div className="dash2-side">
        <SeverityMixCard incidents={incidents} />
        <CimtOnCallCard onViewRoster={onSettings ? () => onSettings('roster') : undefined} />

        {/* Team readiness — training completion + this week's roster coverage */}
        <TeamReadinessCard myTraining={myTraining} onOpenTraining={onTraining} />

        {/* Recently resolved */}
        {recentResolved.length > 0 && (
          <div style={{
            background: 'var(--card)', border: '1px solid var(--border)',
            borderRadius: 16, padding: '18px 18px 16px',
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '.1em',
              textTransform: 'uppercase', color: 'var(--subtle)', marginBottom: 14,
            }}>
              Recently resolved
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              {recentResolved.map(inc => (
                <div key={inc.id} style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                  <span style={{
                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                    background: 'rgba(22,163,74,.12)', color: 'var(--success)',
                    display: 'grid', placeItems: 'center',
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                  </span>
                  <span style={{
                    flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 700, color: 'var(--fg)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{inc.title}</span>
                  <span style={{ fontSize: 12, color: 'var(--subtle)', flexShrink: 0 }}>{timeAgo(inc.updatedAt)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Small dashboard primitives
// ══════════════════════════════════════════════════════════════════════════

function MetricTile({ label, value, sub, tone, filled }: {
  label: string; value: number; sub: string;
  tone: 'brand' | 'danger' | 'warning' | 'success'; filled?: boolean;
}) {
  // Figures read in the page's foreground colour (near-black in light theme)
  // so the row scans as data, not decoration — the one exception is the
  // Critical count, which stays brand-red as a standing alarm.
  const valueColor = filled
    ? '#fff'
    : tone === 'danger' ? 'var(--accent-red)' : 'var(--fg)';
  // Accent the card picks up on hover — tinted to the tile's own role so the
  // Critical tile lifts red, Resolved lifts green, etc.
  const accent: Record<string, string> = {
    brand: 'var(--primary)', danger: 'var(--accent-red)', warning: 'var(--warning)', success: 'var(--success)',
  };
  // A relatable line icon per metric, tucked into the top-right corner.
  const ICONS: Record<string, JSX.Element> = {
    brand:   <><path d="M12 2 2 7l10 5 10-5-10-5z" /><path d="m2 17 10 5 10-5" /><path d="m2 12 10 5 10-5" /></>,
    danger:  <><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
    warning: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />,
    success: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>,
  };
  return (
    <div
      onMouseEnter={(e) => {
        if (filled) return;
        e.currentTarget.style.borderColor = `color-mix(in srgb, ${accent[tone]} 55%, transparent)`;
      }}
      onMouseLeave={(e) => {
        if (filled) return;
        e.currentTarget.style.borderColor = 'var(--border)';
      }}
      style={{
        position: 'relative',
        background: filled ? 'var(--primary)' : 'var(--card)',
        border: filled ? 'none' : '1px solid var(--border)',
        borderRadius: 16, padding: '18px 18px 16px',
        boxShadow: filled ? '0 8px 22px rgba(1,39,112,0.28)' : '0 1px 2px rgba(20,20,45,0.04)',
        transition: 'border-color .14s ease',
      }}>
      {/* Metric icon — top-right corner */}
      <span style={{
        position: 'absolute', top: 15, right: 15,
        width: 32, height: 32, borderRadius: 9, flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: filled ? 'rgba(255,255,255,0.16)' : `color-mix(in srgb, ${accent[tone]} 12%, transparent)`,
        color: filled ? '#fff' : accent[tone],
      }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{ICONS[tone]}</svg>
      </span>
      <div style={{
        position: 'relative', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', paddingRight: 40,
        textTransform: 'uppercase', color: filled ? 'rgba(255,255,255,0.82)' : 'var(--subtle)',
      }}>{label}</div>
      <div className="num-roll" key={value} style={{
        position: 'relative', fontSize: 34, fontWeight: 800, letterSpacing: '-0.03em',
        margin: '10px 0 4px', lineHeight: 1, color: valueColor,
      }}>{value}</div>
      <div style={{
        position: 'relative', fontSize: 12, color: filled ? 'rgba(255,255,255,0.78)' : 'var(--muted)',
      }}>{sub}</div>
    </div>
  );
}

// ── Severity mix ───────────────────────────────────────────────────────────
// A stacked proportion bar + a counted legend for the last 30 days. The bar
// carries the shape of the load at a glance; the legend carries the exact
// numbers. Colour lives on the bar and the legend dots only — the counts
// themselves stay foreground-black so they read as data.
function SeverityMixCard({ incidents }: { incidents: Incident[] }) {
  const WINDOW_DAYS = 30;
  const cutoff = Date.now() - WINDOW_DAYS * 86400000;
  const recent = incidents.filter((i) => new Date(i.createdAt).getTime() >= cutoff);
  const rows = [
    { label: 'Critical', value: recent.filter((i) => i.severity === 'CRITICAL').length, color: 'var(--accent-red)' },
    { label: 'Moderate', value: recent.filter((i) => i.severity === 'MODERATE').length, color: 'var(--warning)' },
    { label: 'Minor',    value: recent.filter((i) => i.severity === 'MINOR').length,    color: 'var(--success)' },
  ];
  const total = recent.length;

  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--border)',
      borderRadius: 16, padding: '18px 18px 16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--subtle)' }}>
          Severity mix
        </span>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>{WINDOW_DAYS} days · {total} total</span>
      </div>

      {/* Stacked proportion bar */}
      <div style={{ display: 'flex', gap: 3, height: 8, marginBottom: 16 }}>
        {total === 0
          ? <div style={{ flex: 1, background: 'var(--border-md)', borderRadius: 3 }} />
          : rows.filter((r) => r.value > 0).map((r) => (
              <div key={r.label} title={`${r.label}: ${r.value}`} style={{ flex: r.value, background: r.color, borderRadius: 3 }} />
            ))}
      </div>

      {/* Counted legend */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        {rows.map((r) => (
          <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: r.color, flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 13, color: 'var(--fg)' }}>{r.label}</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--fg)', letterSpacing: '-0.02em' }}>{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Incident volume graph ────────────────────────────────────────────────────
// A bar per bucket (day or month). Any bucket that contained a critical
// incident burns red; the rest are logged-lavender. Toggle switches the
// bucketing between the recent daily trend and the longer monthly trend.
type VolumeBucket = { label: string; showLabel: boolean; total: number; critical: number };

function bucketByDay(incidents: Incident[], days: number): VolumeBucket[] {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const out: VolumeBucket[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const start = new Date(today); start.setDate(today.getDate() - i);
    const end = new Date(start); end.setDate(start.getDate() + 1);
    const inBucket = incidents.filter((inc) => {
      const t = new Date(inc.createdAt).getTime();
      return t >= start.getTime() && t < end.getTime();
    });
    const idx = days - 1 - i;
    out.push({
      label: start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      showLabel: idx === 0 || idx === days - 1 || idx === Math.floor((days - 1) / 2),
      total: inBucket.length,
      critical: inBucket.filter((inc) => inc.severity === 'CRITICAL').length,
    });
  }
  return out;
}

function bucketByMonth(incidents: Incident[], months: number): VolumeBucket[] {
  const now = new Date();
  const out: VolumeBucket[] = [];
  for (let i = months - 1; i >= 0; i--) {
    const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const inBucket = incidents.filter((inc) => {
      const t = new Date(inc.createdAt).getTime();
      return t >= start.getTime() && t < end.getTime();
    });
    out.push({
      label: start.toLocaleDateString(undefined, { month: 'short' }),
      showLabel: true,
      total: inBucket.length,
      critical: inBucket.filter((inc) => inc.severity === 'CRITICAL').length,
    });
  }
  return out;
}

function IncidentVolumeCard({ incidents }: { incidents: Incident[] }) {
  const [mode, setMode] = useState<'days' | 'months'>('days');
  const buckets = mode === 'days' ? bucketByDay(incidents, 14) : bucketByMonth(incidents, 6);
  const max = Math.max(1, ...buckets.map((b) => b.total));
  const CHART_H = 150;

  // Footer KPIs — derived from real incident data, never fabricated.
  const resolved = incidents.filter((i) => i.status === 'RESOLVED' || i.status === 'CLOSED');
  const resolveHrs = resolved
    .map((i) => (new Date(i.updatedAt).getTime() - new Date(i.createdAt).getTime()) / 3600000)
    .filter((h) => h >= 0);
  const avgResolve = resolveHrs.length ? resolveHrs.reduce((a, b) => a + b, 0) / resolveHrs.length : 0;
  const avgResolveLabel = !resolveHrs.length ? '—'
    : avgResolve >= 24 ? `${(avgResolve / 24).toFixed(1)}d` : `${avgResolve.toFixed(1)}h`;
  const resolutionRate = incidents.length ? Math.round((resolved.length / incidents.length) * 100) : 0;
  const openNow = incidents.filter((i) => i.status === 'OPEN' || i.status === 'IN_PROGRESS' || i.status === 'REOPENED').length;

  const legendSquare = (color: string) => (
    <span style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
  );

  const kpi = (value: string | number, label: string) => (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--fg)', letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4 }}>{label}</div>
    </div>
  );

  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--border)',
      borderRadius: 16, padding: '18px 20px 16px',
    }}>
      {/* Header — title + toggle + legend */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--subtle)' }}>
            Incident volume
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>
            {mode === 'days' ? 'Last 14 days' : 'Last 6 months'}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
          <div style={{ display: 'inline-flex', background: 'var(--surface)', border: '1px solid var(--border-md)', borderRadius: 9, padding: 2 }}>
            {(['days', 'months'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                aria-pressed={mode === m}
                style={{
                  border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: 11.5, fontWeight: 700, padding: '5px 13px', borderRadius: 7,
                  background: mode === m ? 'color-mix(in srgb, var(--primary) 16%, transparent)' : 'transparent',
                  color: mode === m ? 'var(--primary)' : 'var(--muted)',
                  transition: 'background .12s, color .12s',
                }}
              >{m === 'days' ? 'Days' : 'Months'}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 14, fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{legendSquare('color-mix(in srgb, var(--primary) 55%, #fff)')}Logged</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{legendSquare('var(--accent-red)')}Incl. critical</span>
          </div>
        </div>
      </div>

      {/* Bars */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: mode === 'days' ? 6 : 14, height: CHART_H }}>
        {buckets.map((b, i) => {
          const h = b.total > 0 ? Math.max(6, Math.round((b.total / max) * CHART_H)) : 2;
          const crit = b.critical > 0;
          return (
            <div
              key={i}
              title={`${b.label}: ${b.total} logged${crit ? ` · ${b.critical} critical` : ''}`}
              style={{
                flex: 1, height: h, borderRadius: '5px 5px 2px 2px',
                background: crit ? 'var(--accent-red)' : 'color-mix(in srgb, var(--primary) 55%, #fff)',
              }}
            />
          );
        })}
      </div>

      {/* X-axis labels */}
      <div style={{ display: 'flex', gap: mode === 'days' ? 6 : 14, marginTop: 8 }}>
        {buckets.map((b, i) => (
          <div key={i} style={{
            flex: 1, fontSize: 10.5, color: 'var(--subtle)', whiteSpace: 'nowrap',
            textAlign: i === 0 ? 'left' : i === buckets.length - 1 ? 'right' : 'center',
          }}>{b.showLabel ? b.label : ''}</div>
        ))}
      </div>

      {/* KPI footer */}
      <div style={{
        display: 'flex', gap: 32, marginTop: 18, paddingTop: 16,
        borderTop: '1px solid var(--border)',
      }}>
        {kpi(avgResolveLabel, 'Avg. time to resolve')}
        {kpi(`${resolutionRate}%`, 'Resolution rate')}
        {kpi(openNow, 'Currently open')}
      </div>
    </div>
  );
}

function SectionHeader({ title, count, action }: { title: string; count: number; action?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <h3 style={{
        fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 700,
        color: 'var(--fg)', textTransform: 'uppercase', letterSpacing: 0.6,
      }}>{title}</h3>
      {count > 0 && (
        <span style={{
          background: 'var(--accent-red)', color: '#fff',
          fontSize: 10, fontWeight: 700,
          padding: '2px 8px', borderRadius: 999,
          minWidth: 22, textAlign: 'center',
        }}>{count}</span>
      )}
      {action && <div style={{ marginLeft: 'auto' }}>{action}</div>}
    </div>
  );
}

function EmptyTile({ text }: { text: string }) {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px dashed var(--border-md)',
      borderRadius: 12, padding: '22px 16px',
      fontSize: 13, color: 'var(--muted)', textAlign: 'center',
    }}>{text}</div>
  );
}

// Map each incident type to a small inline icon — gives the row a
// type-coded visual anchor on the left so a column of incidents doesn't
// read as five identical white cards. Icons inherit the parent colour so
// they tint to the type's brand hue.
function typeIcon(type: string) {
  const props = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (type) {
    case 'FIRE_EVACUATION':  return <svg {...props}><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>;
    case 'WORKPLACE_INJURY': return <svg {...props}><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>;
    case 'DATA_BREACH':      return <svg {...props}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="9.5" y1="12" x2="14.5" y2="12"/></svg>;
    case 'IT_OUTAGE':        return <svg {...props}><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>;
    case 'SECURITY_THREAT':  return <svg {...props}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>;
    case 'ENVIRONMENTAL':    return <svg {...props}><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>;
    default:                 return <svg {...props}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
  }
}

// Per-type accent colour for the left icon tile. Picks a hue that hints
// at the category (red for fire/security, blue for cyber, amber for
// outage, green for environmental, purple for unknown) without using the
// same colour as severity (which the chip below already carries).
const TYPE_TONE: Record<string, string> = {
  FIRE_EVACUATION:  'var(--accent-red)',
  WORKPLACE_INJURY: 'var(--warning)',
  DATA_BREACH:      '#2563EB',
  IT_OUTAGE:        '#CA8A04',
  SECURITY_THREAT:  'var(--primary)',
  ENVIRONMENTAL:    'var(--success)',
  OTHER:            '#6B7280',
  UNCLASSIFIED:     '#6B7280',
};

function IncidentRow({ inc, onOpen, onEdit, onClose, updating }: {
  inc: Incident; onOpen: () => void;
  onEdit?: () => void; onClose?: () => void; updating: boolean;
}) {
  const sevColour    = SEVERITY_COLOURS[inc.severity] || '#9ca3af';
  const statusColour = STATUS_TEXT[inc.status] || '#9ca3af';
  const typeColour   = TYPE_TONE[inc.type] || '#6B7280';
  // Friendlier than IN_PROGRESS / RESOLVED in screaming caps inside a chip
  const statusLabel  = inc.status === 'IN_PROGRESS'
    ? 'In progress'
    : inc.status.charAt(0) + inc.status.slice(1).toLowerCase();

  const dotPill = (label: string, color: string) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 10.5, fontWeight: 700, lineHeight: 1.4,
      letterSpacing: 0.3, textTransform: 'uppercase',
      padding: '2px 8px', borderRadius: 999,
      color, background: `color-mix(in srgb, ${color} 14%, transparent)`,
      border: `1px solid color-mix(in srgb, ${color} 32%, transparent)`,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );

  return (
    <div
      className="card-refined incident-row"
      style={{
        display: 'flex', alignItems: 'stretch', gap: 0,
        background: 'var(--card)', border: '1px solid var(--border)',
        borderRadius: 14, padding: 0, overflow: 'hidden',
        position: 'relative',
      }}
    >
      <button onClick={onOpen} style={{
        flex: 1, minWidth: 0, textAlign: 'left',
        background: 'transparent', border: 'none', cursor: 'pointer',
        padding: '14px 14px 14px 14px', fontFamily: 'inherit', color: 'var(--fg)',
        display: 'flex', alignItems: 'center', gap: 14,
      }}>
        {/* Type-coded icon tile — same colour family as the type chip
            below so the row is "claim-coded" by type without screaming
            severity twice. */}
        <span style={{
          width: 38, height: 38, borderRadius: 10, flexShrink: 0,
          background: `color-mix(in srgb, ${typeColour} 12%, transparent)`,
          color: typeColour,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid color-mix(in srgb, ${typeColour} 22%, transparent)`,
        }}>
          {typeIcon(inc.type)}
        </span>

        <span style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, flex: 1 }}>
          <span style={{
            fontSize: 14.5, fontWeight: 700, color: 'var(--fg)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            lineHeight: 1.3, letterSpacing: '-0.005em',
          }}>{inc.title}</span>

          {/* Compact meta line: type label · location (if present). The
              chips below carry severity + status — keeping that out of
              this line keeps it scannable as a one-glance subtitle. */}
          <span style={{
            fontSize: 11.5, color: 'var(--muted)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{ color: typeColour, fontWeight: 600 }}>{typeLabel(inc.type)}</span>
            {inc.location && (
              <>
                <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--subtle)' }} />
                <span>{inc.location}</span>
              </>
            )}
          </span>

          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 2 }}>
            {dotPill(inc.severity, sevColour)}
            {dotPill(statusLabel, statusColour)}
          </span>
        </span>
      </button>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '14px 16px 14px 8px', flexShrink: 0,
      }}>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3,
        }}>
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--muted)',
            fontWeight: 600, letterSpacing: 0.2,
          }}>{inc.id}</span>
          <span style={{ fontSize: 11, color: 'var(--subtle)' }}>{timeAgo(inc.createdAt)}</span>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {onEdit && (
            <button onClick={onEdit} style={{
              background: 'transparent', border: '1px solid var(--primary)',
              borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 600,
              color: 'var(--primary)', cursor: 'pointer', fontFamily: 'inherit',
              transition: 'background 0.12s, color 0.12s',
            }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--primary)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--primary)'; }}
            >Edit</button>
          )}
          {onClose && inc.status !== 'CLOSED' && (
            <button onClick={onClose} disabled={updating} style={{
              background: 'transparent', border: '1px solid var(--primary)',
              borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 600,
              color: 'var(--primary)', cursor: updating ? 'wait' : 'pointer', fontFamily: 'inherit',
              transition: 'background 0.12s, color 0.12s',
            }}
              onMouseEnter={(e) => { if (updating) return; e.currentTarget.style.background = 'var(--primary)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--primary)'; }}
            >{updating ? '…' : 'Close'}</button>
          )}
          <button onClick={onOpen} style={{
            background: 'var(--surface)', border: '1px solid var(--primary)',
            borderRadius: 8, padding: '5px 14px', fontSize: 12, fontWeight: 600,
            color: 'var(--primary)', cursor: 'pointer', fontFamily: 'inherit',
            transition: 'background 0.12s, color 0.12s',
          }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--primary)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.color = 'var(--primary)'; }}
          >Open</button>
        </div>
      </div>
    </div>
  );
}

// ── Team readiness ───────────────────────────────────────────────────────────
// Folds the old "Training due" tile into a single readiness panel: a composite
// ring, a plain-language summary of what's still open, and the two figures the
// ring is built from. Everything is derived from data the console already
// trusts — the current user's training assignments and the live weekly roster —
// so the ring never shows a number we can't back up.

// Monday of the current week, YYYY-MM-DD (matches the roster API + CimtOnCall).
function currentWeekStart(): string {
  const d = new Date();
  const day = d.getDay();                 // 0 Sun .. 6 Sat
  const diff = day === 0 ? -6 : 1 - day;  // back to Monday
  d.setDate(d.getDate() + diff);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function ReadinessRing({ pct }: { pct: number }) {
  const size = 78, stroke = 9;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const off = circ * (1 - Math.max(0, Math.min(100, pct)) / 100);
  const tone = pct >= 85 ? 'var(--success)' : pct >= 60 ? 'var(--primary)' : 'var(--accent-red)';
  return (
    <span style={{ position: 'relative', width: size, height: size, flexShrink: 0, display: 'inline-block' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border-md)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={tone} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={off} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset .5s ease' }}
        />
      </svg>
      <span style={{
        position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
        fontSize: 19, fontWeight: 800, color: 'var(--fg)', letterSpacing: '-0.03em',
      }}>{pct}%</span>
    </span>
  );
}

function ReadinessRow({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 800, color: tone }}>{value}</span>
    </div>
  );
}

function TeamReadinessCard({ myTraining, onOpenTraining }: {
  myTraining: TrainingAssignment[];
  onOpenTraining?: () => void;
}) {
  const [gaps, setGaps]           = useState<number | null>(null);
  const [roleCount, setRoleCount] = useState(0);

  useEffect(() => {
    let alive = true;
    getRoster(currentWeekStart())
      .then((r) => {
        if (!alive) return;
        const roles = r.roster || [];
        setRoleCount(roles.length);
        setGaps(roles.filter((e) => !e.primary).length);   // unfilled primaries
      })
      .catch(() => { if (alive) { setGaps(null); setRoleCount(0); } });
    return () => { alive = false; };
  }, []);

  // Training completion — from the assignments the dashboard already loaded.
  const totalT     = myTraining.length;
  const completedT = myTraining.filter((a) => a.status === 'COMPLETED').length;
  const dueT       = totalT - completedT;
  const overdueT   = myTraining.filter((a) => a.status !== 'COMPLETED' && new Date(a.deadlineAt) < new Date()).length;
  const coverage   = totalT ? Math.round((completedT / totalT) * 100) : 100;

  // Roster coverage for this week — null while loading / on error.
  const rosterPct = roleCount ? Math.round(((roleCount - (gaps ?? 0)) / roleCount) * 100) : null;

  // Composite readiness — training weighted a little heavier than roster, since
  // an untrained commander is the bigger risk. Falls back to training alone
  // until the roster resolves.
  const readiness = rosterPct == null ? coverage : Math.round(coverage * 0.65 + rosterPct * 0.35);

  // Plain-language summary of what's still open this week.
  const openParts: React.ReactNode[] = [];
  if (dueT > 0) openParts.push(
    <span key="t" style={{ fontWeight: 700, color: overdueT > 0 ? 'var(--accent-red)' : 'var(--warning)' }}>
      {dueT} module{dueT === 1 ? '' : 's'} due
    </span>,
  );
  if ((gaps ?? 0) > 0) openParts.push(
    <span key="r" style={{ fontWeight: 700, color: 'var(--accent-red)' }}>
      {gaps} roster gap{gaps === 1 ? '' : 's'}
    </span>,
  );

  const rosterValue = gaps == null ? '—'
    : gaps === 0 ? 'Fully staffed'
    : `${gaps} gap${gaps === 1 ? '' : 's'}`;

  return (
    <button
      onClick={onOpenTraining}
      style={{
        display: 'block', width: '100%', textAlign: 'left',
        cursor: onOpenTraining ? 'pointer' : 'default',
        background: 'var(--card)', border: '1px solid var(--border)',
        borderRadius: 16, padding: '18px 18px 16px',
        fontFamily: 'inherit', color: 'var(--fg)',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--subtle)', marginBottom: 14 }}>
        Team readiness
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <ReadinessRing pct={readiness} />
        <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>
          {openParts.length === 0
            ? <>Fully ready — training complete and every CIMT role covered this week.</>
            : <>Ready, but {openParts.length === 1 ? openParts[0] : <>{openParts[0]} and {openParts[1]}</>} this week.</>}
        </div>
      </div>

      {/* Underlying figures */}
      <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Training coverage — label · mini bar · value */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12.5, color: 'var(--muted)', flexShrink: 0 }}>Training coverage</span>
          <span style={{ flex: 1, height: 5, borderRadius: 999, background: 'var(--border-md)', overflow: 'hidden', minWidth: 24 }}>
            <span style={{ display: 'block', height: '100%', width: `${coverage}%`, background: 'var(--primary)', borderRadius: 999 }} />
          </span>
          <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--fg)', flexShrink: 0 }}>{coverage}%</span>
        </div>
        <ReadinessRow label="Roster this week" value={rosterValue} tone={(gaps ?? 0) > 0 ? 'var(--accent-red)' : 'var(--fg)'} />
        <ReadinessRow label="Modules due" value={dueT === 0 ? 'All clear' : String(dueT)} tone={overdueT > 0 ? 'var(--accent-red)' : 'var(--fg)'} />
      </div>
    </button>
  );
}



// ════════════════════════════════════════════════════════════════════════════
// Live-incident command console
// A denser operational view for when something is actually running. Every figure
// is derived from data the console already trusts — the incident list and the
// live weekly roster. Widgets that would need data we don't hold (per-incident
// owners, CIMT join states, action assignments) are left out rather than faked.
// ════════════════════════════════════════════════════════════════════════════

// Acknowledge-SLA targets, in minutes, by severity. A live incident is
// "breaching" once its age passes the target for its severity.
const SLA_TARGET_MIN: Record<string, number> = { CRITICAL: 15, MODERATE: 60, MINOR: 240 };

function slaFor(inc: Incident) {
  const ageMin = (Date.now() - new Date(inc.createdAt).getTime()) / 60000;
  const target = SLA_TARGET_MIN[inc.severity] ?? 240;
  const delta  = Math.round(ageMin - target);      // >0 = breached by delta mins
  return { breached: delta > 0, minutes: Math.abs(delta) };
}

function fmtMins(m: number): string {
  if (m < 60) return `${m}m`;
  if (m < 1440) {                       // under 24h → hours + minutes
    const h = Math.floor(m / 60), mm = m % 60;
    return mm ? `${h}h ${mm}m` : `${h}h`;
  }
  const d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60);   // 24h+ → days + hours
  return h ? `${d}d ${h}h` : `${d}d`;
}

function fmtClock(iso: string): string {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const mm = String(m).padStart(2, '0'), ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

function slaPill(inc: Incident): { text: string; breached: boolean } {
  const sla = slaFor(inc);
  return { text: sla.breached ? `Breached +${fmtMins(sla.minutes)}` : `${fmtMins(sla.minutes)} left`, breached: sla.breached };
}

// Re-render on a fixed cadence so live clocks + SLA counters stay honest.
function useNow(intervalMs: number) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
}

function RailVital({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
      <span style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</span>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: tone || 'var(--fg)' }}>{value}</span>
    </div>
  );
}

function LiveCommandPanel({ incident, onOpenChat }: { incident?: Incident; onOpenChat: (i: Incident) => void }) {
  useNow(1000);   // tick the elapsed clock

  if (!incident) {
    return (
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: '20px 22px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <span style={{ width: 42, height: 42, borderRadius: '50%', background: 'var(--success-soft)', color: 'var(--success)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
        </span>
        <div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: 'var(--fg)' }}>Console on standby</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>No live incident in command. This view takes over the moment a critical incident is reported.</div>
        </div>
      </div>
    );
  }

  const sla = slaPill(incident);
  const statusLabel = incident.status === 'IN_PROGRESS' ? 'In progress' : incident.status.charAt(0) + incident.status.slice(1).toLowerCase();

  return (
    <div style={{ background: 'var(--card)', border: '1px solid color-mix(in srgb, var(--accent-red) 22%, var(--border))', borderRadius: 16, overflow: 'hidden' }}>
      {/* Command strip */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        padding: '10px 20px', background: 'color-mix(in srgb, var(--accent-red) 7%, var(--card))',
        borderBottom: '1px solid color-mix(in srgb, var(--accent-red) 14%, transparent)',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 11, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--accent-red)' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent-red)', animation: 'cimaCriticalDotPulse 1.6s infinite' }} />
          Live incident command
          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--muted)', letterSpacing: 0 }}>{incident.id}</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--subtle)' }}>Elapsed</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 15, fontWeight: 700, color: 'var(--fg)', fontVariantNumeric: 'tabular-nums' }}>{fmtClock(incident.createdAt)}</span>
        </span>
      </div>

      {/* Body — detail (left) + derived rail (right) */}
      <div className="live-command-body">
        <div style={{ flex: 1, minWidth: 0, padding: '20px 22px' }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 'var(--display-md)', fontWeight: 700, color: 'var(--fg)', lineHeight: 1.15, letterSpacing: '-0.015em' }}>{incident.title}</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 8 }}>
            <span style={{ color: 'var(--subtle)' }}>Location</span> {incident.location || '—'}
            <span style={{ margin: '0 8px', color: 'var(--subtle)' }}>·</span>
            <span style={{ color: 'var(--subtle)' }}>Type</span> {typeLabel(incident.type)}
            {incident.peopleInvolved && (
              <>
                <span style={{ margin: '0 8px', color: 'var(--subtle)' }}>·</span>
                <span style={{ color: 'var(--subtle)' }}>Affected</span> {incident.peopleInvolved}
              </>
            )}
          </div>

          {incident.description && (
            <div style={{ marginTop: 16, borderRadius: 12, padding: '14px 16px', background: 'color-mix(in srgb, var(--primary) 7%, var(--card))', border: '1px solid color-mix(in srgb, var(--primary) 18%, transparent)' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--primary)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 9.5, fontWeight: 800 }}>AI</span>
                <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--primary)' }}>Auto SitRep</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--fg)', lineHeight: 1.55 }}>{incident.description}</div>
            </div>
          )}

          <button onClick={() => onOpenChat(incident)} style={{
            marginTop: 18, display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'var(--primary)', color: '#fff',
            padding: '11px 18px', borderRadius: 10, fontSize: 13.5, fontWeight: 700,
            border: 'none', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(0,103,254,0.26)',
          }}>
            Open SitRep
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
          </button>
        </div>

        <div className="live-command-rail">
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--subtle)', marginBottom: 10 }}>Acknowledge SLA</div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 13px', borderRadius: 10, fontSize: 13, fontWeight: 700,
            color: sla.breached ? 'var(--accent-red)' : 'var(--success)',
            background: sla.breached ? 'color-mix(in srgb, var(--accent-red) 12%, transparent)' : 'var(--success-soft)',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
            {sla.text}
          </div>

          <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <RailVital label="Severity" value={incident.severity} tone={SEVERITY_COLOURS[incident.severity]} />
            <RailVital label="Reported" value={timeAgo(incident.createdAt)} />
            <RailVital label="Status"   value={statusLabel} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── All Incidents Modal ────────────────────────────────────────────────
// Replaces the old Appearance tile. Shows a breakdown of every incident
// grouped by status + severity + type, with click-through into any row.
function AllIncidentsModal({ incidents, onClose, onOpen }: {
  incidents: Incident[];
  onClose:   () => void;
  onOpen:    (i: Incident) => void;
}) {
  const [filter, setFilter] = useState<'' | 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED' | 'MERGED'>('');
  const [sev, setSev]       = useState<'' | 'CRITICAL' | 'MODERATE' | 'MINOR'>('');
  const [q, setQ]           = useState('');

  const stats = {
    total:       incidents.length,
    open:        incidents.filter(i => i.status === 'OPEN').length,
    inProgress:  incidents.filter(i => i.status === 'IN_PROGRESS').length,
    resolved:    incidents.filter(i => i.status === 'RESOLVED').length,
    closed:      incidents.filter(i => i.status === 'CLOSED').length,
    merged:      incidents.filter(i => i.status === 'MERGED').length,
    critical:    incidents.filter(i => i.severity === 'CRITICAL').length,
    moderate:    incidents.filter(i => i.severity === 'MODERATE').length,
    minor:       incidents.filter(i => i.severity === 'MINOR').length,
  };

  const byType: Record<string, number> = {};
  for (const i of incidents) {
    byType[i.type] = (byType[i.type] || 0) + 1;
  }

  const filtered = incidents.filter(i => {
    if (filter && i.status !== filter) return false;
    if (sev && i.severity !== sev) return false;
    if (q) {
      const ql = q.toLowerCase();
      if (!i.id.toLowerCase().includes(ql)
        && !i.title.toLowerCase().includes(ql)
        && !(i.location || '').toLowerCase().includes(ql)) return false;
    }
    return true;
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // ── Sub-pieces ────────────────────────────────────────────────────────
  // Single stat tile — value top, label bottom, label is muted gray so the
  // colored count carries the visual weight without screaming.
  const Stat = ({ label, value, color }: { label: string; value: number; color: string }) => (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border-md)',
      borderRadius: 10, padding: '10px 12px', minWidth: 96, flex: '0 0 auto',
    }}>
      <div style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1.05 }}>{value}</div>
      <div style={{
        fontSize: 10.5, color: 'var(--muted)', marginTop: 4,
        textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600,
      }}>{label}</div>
    </div>
  );

  // Chip used for severity/status/type in each row + the by-type breakdown.
  // Dot variant adds a coloured dot on the left, ghost variant is filled.
  const Chip = ({
    label, color, variant = 'tinted', dot,
  }: { label: string; color: string; variant?: 'tinted' | 'ghost'; dot?: boolean }) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 10.5, fontWeight: 700, lineHeight: 1.4,
      letterSpacing: 0.3, textTransform: 'uppercase',
      padding: '2px 8px', borderRadius: 999,
      color: variant === 'tinted' ? color : 'var(--fg)',
      background: variant === 'tinted'
        ? `color-mix(in srgb, ${color} 14%, transparent)`
        : 'var(--surface)',
      border: `1px solid color-mix(in srgb, ${color} 32%, transparent)`,
      whiteSpace: 'nowrap',
    }}>
      {dot && (
        <span style={{
          width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0,
        }} />
      )}
      {label}
    </span>
  );

  // Status pill copy is friendlier ("In progress" not "IN_PROGRESS")
  const statusLabel = (s: string) =>
    s === 'IN_PROGRESS' ? 'In progress'
    : s.charAt(0) + s.slice(1).toLowerCase();

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1004,
        background: 'rgba(12,12,18,0.62)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card)', border: '1px solid var(--border-md)',
          borderRadius: 14, maxWidth: 980, width: '96%', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 22px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h3 style={{
              fontFamily: 'var(--font-heading)', fontSize: 19, fontWeight: 700,
              margin: 0, color: 'var(--fg)', letterSpacing: -0.1,
            }}>
              All Incidents
            </h3>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
              {stats.total} total · {filtered.length === stats.total
                ? 'showing everything'
                : `${filtered.length} match the current filter`}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent', border: 'none', color: 'var(--muted)',
              fontSize: 24, cursor: 'pointer', padding: 4, lineHeight: 1,
              borderRadius: 6,
            }}
          >×</button>
        </div>

        {/* Stats — two semantic groups (status / severity) split by a vertical divider. */}
        <div style={{
          padding: '14px 22px', borderBottom: '1px solid var(--border)',
          display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'stretch',
        }}>
          <Stat label="Open"        value={stats.open}       color="var(--fg)" />
          <Stat label="In progress" value={stats.inProgress} color="var(--fg)" />
          <Stat label="Resolved"    value={stats.resolved}   color="#22c55e" />
          <Stat label="Closed"      value={stats.closed}     color="#94a3b8" />
          {stats.merged > 0 && <Stat label="Merged" value={stats.merged} color="var(--fg)" />}
          <div style={{
            width: 1, alignSelf: 'stretch', margin: '4px 4px',
            background: 'var(--border-md)',
          }} />
          <Stat label="Critical"    value={stats.critical}   color="#dc2626" />
          <Stat label="Moderate"    value={stats.moderate}   color="var(--fg)" />
          <Stat label="Minor"       value={stats.minor}      color="var(--fg)" />
        </div>

        {/* Type breakdown — chip row instead of dot-separated text. Click a
            chip to filter (handy when you spot one type dominating). */}
        {Object.keys(byType).length > 0 && (
          <div style={{
            padding: '12px 22px', borderBottom: '1px solid var(--border)',
            display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
          }}>
            <span style={{
              fontSize: 10.5, color: 'var(--muted)', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 4,
            }}>
              By type
            </span>
            {Object.entries(byType)
              .sort((a, b) => b[1] - a[1])
              .map(([t, n]) => (
                <span
                  key={t}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 11.5, color: 'var(--fg)',
                    background: 'var(--surface)',
                    border: '1px solid var(--border-md)',
                    padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ color: 'var(--muted)' }}>{typeLabel(t)}</span>
                  <span style={{ fontWeight: 700 }}>{n}</span>
                </span>
              ))}
          </div>
        )}

        {/* Filters */}
        <div style={{
          padding: '12px 22px', borderBottom: '1px solid var(--border)',
          display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
        }}>
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by ID / title / location…"
            style={{
              flex: 1, minWidth: 220, background: 'var(--surface)',
              border: '1px solid var(--border-md)', borderRadius: 8, padding: '8px 12px',
              fontSize: 13, color: 'var(--fg)', fontFamily: 'inherit',
            }}
          />
          <select value={filter} onChange={(e) => setFilter(e.target.value as any)} style={{
            background: 'var(--surface)', border: '1px solid var(--border-md)', borderRadius: 8,
            padding: '8px 10px', fontSize: 13, color: 'var(--fg)', fontFamily: 'inherit', cursor: 'pointer',
          }}>
            <option value="">All statuses</option>
            <option value="OPEN">Open</option>
            <option value="IN_PROGRESS">In progress</option>
            <option value="RESOLVED">Resolved</option>
            <option value="CLOSED">Closed</option>
            <option value="MERGED">Merged</option>
          </select>
          <select value={sev} onChange={(e) => setSev(e.target.value as any)} style={{
            background: 'var(--surface)', border: '1px solid var(--border-md)', borderRadius: 8,
            padding: '8px 10px', fontSize: 13, color: 'var(--fg)', fontFamily: 'inherit', cursor: 'pointer',
          }}>
            <option value="">All severities</option>
            <option value="CRITICAL">Critical</option>
            <option value="MODERATE">Moderate</option>
            <option value="MINOR">Minor</option>
          </select>
        </div>

        {/* List — each row: severity strip · title + chip row · ID + time on
            the right. Right-aligned ID/time stops the meta line from
            wrapping and lets the eye scan a column for one of them. */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div style={{
              padding: '40px 22px', color: 'var(--muted)', fontSize: 13,
              textAlign: 'center',
            }}>
              No incidents match these filters.
            </div>
          ) : filtered.map((i) => {
            const sevCol = SEVERITY_COLOURS[i.severity] || 'var(--muted)';
            const statusCol = STATUS_TEXT[i.status] || 'var(--muted)';
            return (
              <button
                key={i.id}
                onClick={() => onOpen(i)}
                style={{
                  width: '100%', textAlign: 'left', cursor: 'pointer',
                  background: 'transparent', border: 'none',
                  borderBottom: '1px solid var(--border)',
                  padding: '12px 22px',
                  display: 'flex', alignItems: 'center', gap: 14,
                  fontFamily: 'inherit', transition: 'background 0.12s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 14, fontWeight: 600, color: 'var(--fg)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    lineHeight: 1.35,
                  }}>
                    {i.title}
                  </div>
                  <div style={{
                    marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap',
                    alignItems: 'center',
                  }}>
                    <Chip label={i.severity}             color={sevCol}    />
                    <Chip label={statusLabel(i.status)}  color={statusCol} />
                    <Chip label={typeLabel(i.type)}      color="var(--primary)"   variant="ghost" />
                  </div>
                </div>
                <div style={{
                  flexShrink: 0, textAlign: 'right',
                  display: 'flex', flexDirection: 'column', gap: 3,
                }}>
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--muted)',
                    fontWeight: 600, letterSpacing: 0.2,
                  }}>
                    {i.id}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--subtle)' }}>
                    {timeAgo(i.createdAt)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
